import { createEntityAdapter, createSlice } from '@reduxjs/toolkit'
import { StoreData } from '../reducers/typings'
import { IKnockStatus } from './typings'
import {
  addKnockStatusHistory,
  createKnockStatus,
  createKnockStatusLocation,
  createKnockStatusLocationBase,
  deleteKnockStatus,
  getAllKnockStatuses,
  updateKnockStatus,
  updateKnockStatusHistoryById,
} from './api'

const knockStatusAdapter = createEntityAdapter<IKnockStatus>({
  sortComparer: (a, b) => {
    // System status always first, then by displayOrder, then by name
    if (a.isSystem && !b.isSystem) return -1
    if (!a.isSystem && b.isSystem) return 1
    if (a.displayOrder != null && b.displayOrder != null) {
      return a.displayOrder - b.displayOrder
    }
    return a.name.localeCompare(b.name)
  },
})

/**
 * `knockStatusSavedLocationId` is the location of the most recent save, so a
 * callback can tell "this bubble's location" from "some other open bubble" (SH-1781).
 */
export const initialState = knockStatusAdapter.getInitialState({
  status: 'idle' as 'idle' | 'loaded',
  knockStatusDeletedAt: null as number | null,
  knockStatusSavedAt: null as number | null,
  knockStatusSavedLocationId: null as number | null,
})

const knockStatusSlice = createSlice({
  name: 'knockStatus',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getAllKnockStatuses.fulfilled, (state, { payload }) => {
      knockStatusAdapter.setAll(state, payload)
      state.status = 'loaded'
    })

    builder.addCase(createKnockStatus.fulfilled, (state, { payload }) => {
      knockStatusAdapter.addOne(state, payload)
    })

    builder.addCase(updateKnockStatus.fulfilled, (state, { payload }) => {
      knockStatusAdapter.upsertOne(state, payload)
    })

    builder.addCase(deleteKnockStatus.fulfilled, (state, { payload }) => {
      knockStatusAdapter.removeOne(state, payload)
      state.knockStatusDeletedAt = Date.now()
    })

    // Any knock status save (create or update history) triggers a tile refresh
    // so contactLocations picks up the new badge data immediately. Each case
    // also records which location it was for (SH-1781) -- the response shape
    // differs per endpoint, so the id field is read per-case rather than
    // through one shared reducer.
    builder.addCase(
      createKnockStatusLocation.fulfilled,
      (state, { payload }) => {
        state.knockStatusSavedAt = Date.now()
        state.knockStatusSavedLocationId = payload.knockStatusLocationId
      },
    )
    builder.addCase(
      createKnockStatusLocationBase.fulfilled,
      (state, { payload }) => {
        state.knockStatusSavedAt = Date.now()
        state.knockStatusSavedLocationId = payload.id ?? null
      },
    )
    builder.addCase(addKnockStatusHistory.fulfilled, (state, { payload }) => {
      state.knockStatusSavedAt = Date.now()
      state.knockStatusSavedLocationId = payload.knockStatusLocationId
    })
    builder.addCase(
      updateKnockStatusHistoryById.fulfilled,
      (state, { payload }) => {
        state.knockStatusSavedAt = Date.now()
        state.knockStatusSavedLocationId = payload.knockStatusLocationId
      },
    )
  },
})

const knockStatusAdapterSelectors = knockStatusAdapter.getSelectors<StoreData>(
  (state) => state.knockStatus,
)

export const {
  selectAll: selectAllKnockStatuses,
  selectById: selectKnockStatusById,
} = knockStatusAdapterSelectors

export const selectKnockStatusesLoaded = (state: StoreData) =>
  state.knockStatus.status === 'loaded'

export const selectKnockStatusDeletedAt = (state: StoreData) =>
  state.knockStatus.knockStatusDeletedAt

export const selectKnockStatusSavedAt = (state: StoreData) =>
  state.knockStatus.knockStatusSavedAt

export const selectKnockStatusSavedLocationId = (state: StoreData) =>
  state.knockStatus.knockStatusSavedLocationId

export default knockStatusSlice.reducer
